import { createApi } from '@reduxjs/toolkit/query/react';
import createBaseQuery from '../../utils/baseQuery';

const baseQueryWithReAuth = createBaseQuery('media');

export const mediaApiSlice = createApi({
  reducerPath: 'mediaApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['Videos', 'Audios', 'Documents'],
  endpoints: (builder) => ({
    getMediaType: builder.query({
      query: () => ({
        url: '/get-media-type',
        method: 'GET'
      })
    }),
    getMediaVisibility: builder.query({
      query: () => ({
        url: '/get-media-visibility',
        method: 'GET'
      })
    }),
    getAudioData: builder.query({
      query: () => ({
        url: '/get-audio-data',
        method: 'GET'
      }),
      providesTags: ['Audios']
    }),
    getVideoData: builder.query({
      query: ({ page = 1, limit = 10 }) => ({
        url: '/get-all-videos',
        method: 'GET',
        params: { page, limit }
      }),
      providesTags: ['Videos']
    }),
    getDocumentsByVideoId: builder.query({
      query: (videoId) => ({
        url: '/get-documents-by-video-id',
        method: 'GET',
        params: { videoId }
      })
    }),
    getSuggestedVideos: builder.query({
      query: (videoInfo) => ({
        url: '/get-video',
        method: 'POST',
        body: videoInfo
      })
    }),
    deleteAudioById: builder.mutation({
      query: (audioId) => ({
        url: '/delete-audio',
        method: 'DELETE',
        params: { audioId }
      }),
      invalidatesTags: ['Audios']
    }),
    deleteVideoById: builder.mutation({
      query: (videoId) => ({
        url: '/delete-video',
        method: 'DELETE',
        params: { videoId }
      }),
      invalidatesTags: ['Videos']
    }),
    deleteDocumentById: builder.mutation({
      query: (documentId) => ({
        url: `/delete-document`,
        method: 'DELETE',
        params: { documentId }
      }),
      invalidatesTags: ['Documents']
    }),
    getMediaDownloadLink: builder.query({
      query: (mediaId) => ({
        url: '/get-media-download-link',
        method: 'GET',
        params: { mediaId }
      })
    }),
    getCaption: builder.query({
      query: (videoIndexerId) => ({
        url: '/get-captions',
        method: 'GET',
        params: { videoIndexerId }
      })
    }),
    getIndexcerProgress: builder.query({
      query: (videoIndexerId) => ({
        url: '/get-video-indexer-progress',
        method: 'GET',
        params: { videoIndexerId }
      })
    }),
    getIndexcerData: builder.mutation({
      query: (indexData) => ({
        url: '/get-indexer-info',
        method: 'POST',
        body: indexData
      })
    }),
    getEcclesialDocuments: builder.query({
      query: ({ type }) => ({
        url: '/documents',
        method: 'GET',
        params: { type }
      }),
      providesTags: ['Documents']
    }),

    initializeUpload: builder.mutation({
      query: (payload) => ({
        url: '/initialize-upload',
        method: 'POST',
        body: payload
      })
    }),

    uploadChunk: builder.mutation({
      query: ({ formData, onUploadProgress }) => ({
        url: '/upload-chunck',
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
      })
    }),

    uploadVideoOrAudio: builder.mutation({
      query: ({ formData, isVideo, onUploadProgress }) => ({
        url: `/upload-${isVideo ? 'video' : 'audio'}`,
        method: 'POST',
        body: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
      })
    }),
    searchVideos: builder.mutation({
      query: (payload) => ({
        url: '/search-video',
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Videos']
    })
  })
});

export const {
  useGetCaptionQuery,
  useGetMediaTypeQuery,
  useGetAudioDataQuery,
  useGetVideoDataQuery,
  useUploadChunkMutation,
  useSearchVideosMutation,
  useGetIndexcerDataMutation,
  useDeleteVideoByIdMutation,
  useDeleteAudioByIdMutation,
  useGetSuggestedVideosQuery,
  useGetMediaVisibilityQuery,
  useGetIndexcerProgressQuery,
  useInitializeUploadMutation,
  useGetMediaDownloadLinkQuery,
  useGetDocumentsByVideoIdQuery,
  useGetEcclesialDocumentsQuery,
  useDeleteDocumentByIdMutation,
  useUploadVideoOrAudioMutation
} = mediaApiSlice;
